import { useState } from "react";
import { Link } from "react-router-dom";
import { ScrollReveal } from "@/components/base/ScrollAnimations";
import { programmes } from "@/mocks/homeData";

const wingColors: Record<string, string> = {
  "Innovation Wing": "bg-tie-teal/10 border-tie-teal/20",
  "Research Wing": "bg-tie-navy/5 border-tie-navy/10",
  "Readiness & Recognition": "bg-amber-50 border-amber-100",
  "Expo Wing": "bg-violet-50 border-violet-100",
};

const wingIcons: Record<string, string> = {
  "Innovation Wing": "ri-flashlight-line",
  "Research Wing": "ri-microscope-line",
  "Readiness & Recognition": "ri-award-line",
  "Expo Wing": "ri-store-2-line",
};

const programmeRoutes: Record<string, string> = {
  PIIC: "/piic",
  PTIM: "/ptim",
  Training: "/training",
  "TIE Experts' Platform": "/experts",
  PIARC: "/piarc",
  IJTAI: "/ijtai",
  AIRCon: "/aircon",
};

export default function ProgrammesSection() {
  const [activeWing, setActiveWing] = useState("Innovation Wing");

  const activeProgramme = programmes.find((p) => p.wing === activeWing);

  return (
    <section id="programmes" className="bg-white py-16 md:py-24 lg:py-32">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Header */}
        <ScrollReveal>
          <div className="max-w-3xl mb-10 md:mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-tie-teal/10 mb-4 md:mb-6">
              <span className="text-xs font-semibold text-tie-teal uppercase tracking-wider">
                / Programmes
              </span>
            </div>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-5xl font-bold text-tie-text-primary leading-tight">
              Four Wings. One Mission.
            </h2>
            <p className="mt-3 md:mt-4 text-base md:text-lg text-tie-text-secondary">
              TIE operates through four interconnected wings that together
              institutionalize innovation across Bangladesh's textile ecosystem.
            </p>
          </div>
        </ScrollReveal>

        {/* Wing Tabs */}
        <ScrollReveal delay={100}>
          <div className="flex flex-wrap gap-2 md:gap-3 mb-8 md:mb-12">
            {programmes.map((prog) => (
              <button
                key={prog.wing}
                onClick={() => setActiveWing(prog.wing)}
                className={`px-4 md:px-6 py-2.5 md:py-3 rounded-full text-xs md:text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                  activeWing === prog.wing
                    ? "bg-tie-navy text-white"
                    : "bg-tie-grey text-tie-text-secondary hover:bg-tie-grey-dark"
                }`}
              >
                <i className={`${wingIcons[prog.wing]} mr-1.5 md:mr-2`} />
                {prog.wing}
              </button>
            ))}
          </div>
        </ScrollReveal>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {activeProgramme?.items.map((item, index) => {
            const route = programmeRoutes[item.name];
            return (
              <ScrollReveal key={item.name} delay={index * 100}>
                {route ? (
                  <Link to={route} className="block group h-full cursor-pointer">
                    <div
                      className={`h-full p-5 md:p-6 rounded-xl border transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
                        wingColors[activeWing]
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3 md:mb-4">
                        <span className="text-xs font-bold text-tie-teal uppercase tracking-wider">
                          {item.name}
                        </span>
                        <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <i className="ri-arrow-right-line text-tie-teal text-sm" />
                        </div>
                      </div>
                      <h3 className="text-base md:text-lg font-semibold text-tie-text-primary mb-2">
                        {item.fullName}
                      </h3>
                      <p className="text-sm text-tie-text-secondary leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </Link>
                ) : (
                  <div
                    className={`group h-full p-5 md:p-6 rounded-xl border transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
                      wingColors[activeWing]
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3 md:mb-4">
                      <span className="text-xs font-bold text-tie-teal uppercase tracking-wider">
                        {item.name}
                      </span>
                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <i className="ri-arrow-right-line text-tie-teal text-sm" />
                      </div>
                    </div>
                    <h3 className="text-base md:text-lg font-semibold text-tie-text-primary mb-2">
                      {item.fullName}
                    </h3>
                    <p className="text-sm text-tie-text-secondary leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                )}
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}